/**
 * @fileoverview
 * Helper function to build appContext for embeds.
 */

import type { Api_AdConfig } from '@core/api'
import type { Channel } from '@core/channel'
import type { FeedSourceConfig } from '@core/feed'
import { logMessage } from '@core/logging'
import { pruneNilValues } from '@core/object'
import type { IAnyWidgetParams } from '@core/types'
import type { AppContextType } from '@embed/components/helpers'

import { getPresentationFromParams } from './getPresentationFromParams'
import { mapConfigToTrackingData } from './mapConfigToTrackingData'
import type { IWidgetAppContext } from './types'

interface IProps<TParams> {
  appContextType: AppContextType
  channel?: Channel | null
  embedInstanceId: string
  params: TParams
  guestId: string
  adConfig?: Api_AdConfig | null
  embedFeedConfig: FeedSourceConfig | null | undefined
}

/**
 *
 */
export function getAppContext<TParams extends IAnyWidgetParams>({
  appContextType,
  channel,
  embedFeedConfig,
  embedInstanceId,
  params,
  guestId,
  adConfig,
}: IProps<TParams>): IWidgetAppContext<TParams> | null {
  if ((params.channel || params.channel_id) && !channel) {
    return null
  }

  if (params.playlist && !(params.channel || params.channel_id)) {
    logMessage(
      'Widget with `playlist` is missing `channel`',
      {
        data: {
          playlist: params.playlist,
        },
      },
      1,
    )

    return null
  }

  const { trackingData, trackingDataPixel } =
    mapConfigToTrackingData(embedFeedConfig, channel) ?? {}

  if (!embedFeedConfig || !trackingData || !trackingDataPixel) {
    return null
  }

  const presentation = getPresentationFromParams(appContextType, params)

  return {
    appContextType,
    params,
    username: channel?.username || params.channel || params.channel_id,
    embedFeedConfig,
    embedInstanceId,
    guestId,
    channel,
    adConfig,
    trackingData: {
      app_context_type: appContextType,
      embed_instance_id: embedInstanceId,
      ...trackingData,
      presentation,
      guest_id: guestId,
      page_url: params.page_url ?? '',
      product: 'embed.web.naboo',
    },
    trackingDataPixel: pruneNilValues({
      app_context_type: appContextType,
      embed_instance_id: embedInstanceId,
      ...trackingDataPixel,
      presentation,
      _widget_config_id: getWidgetConfigId(params?.widget_config_id),
      widget_source: params?.widget_source,
      mode: params?.mode === 'pinned' ? 'pinned' : null,
      _business_id: params?.business_id,
    }),
  }
}

function getWidgetConfigId(widgetConfigId: string | undefined) {
  if (!widgetConfigId) {
    return undefined
  }

  return widgetConfigId.split('_')[0]
}
